import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SubPage } from "@/components/sub-page";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/comunicaciones/media")({
  head: () => ({
    meta: [
      { title: "Multimedia | Mine Class" },
      { name: "description", content: "Fotos y videos de eventos, capacitaciones y actividades de Mine Class." },
      { property: "og:title", content: "Multimedia | Mine Class" },
      { property: "og:description", content: "Galería de fotos y videos de eventos, capacitaciones y actividades de Mine Class." },
      { property: "og:url", content: "https://mine-class.com/comunicaciones/media" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://mine-class.com/comunicaciones/media" }],
  }),
  component: Page,
});

type Folder = { id: string; name: string; parent_id: string | null; display_date: string | null; sort_order: number; created_at: string };
type Asset = {
  id: string;
  folder_id: string | null;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
};

function Page() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [signed, setSigned] = useState<Record<string, string>>({});
  const [openFolder, setOpenFolder] = useState<Folder | null>(null);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    (async () => {
      const [f, a] = await Promise.all([
        supabase.from("media_folders").select("id,name,parent_id,display_date,sort_order,created_at").order("sort_order", { ascending: true }).order("display_date", { ascending: false, nullsFirst: false }).order("created_at", { ascending: false }),
        supabase.from("media_assets").select("id,folder_id,storage_path,file_name,mime_type").order("created_at", { ascending: false }),
      ]);
      const fl = (f.data ?? []) as Folder[];
      const al = (a.data ?? []) as Asset[];
      setFolders(fl);
      setAssets(al);
      const map: Record<string, string> = {};
      await Promise.all(
        al.map(async (asset) => {
          const { data: s } = await supabase.storage.from("media").createSignedUrl(asset.storage_path, 3600);
          if (s?.signedUrl) map[asset.id] = s.signedUrl;
        }),
      );
      setSigned(map);
    })();
  }, []);

  // Show only top-level folders that contain any assets (recursive)
  const folderAssets = useMemo(() => {
    const byFolder: Record<string, Asset[]> = {};
    for (const a of assets) {
      const key = a.folder_id ?? "__root__";
      (byFolder[key] ||= []).push(a);
    }
    return byFolder;
  }, [assets]);

  function assetsInFolderTree(folderId: string): Asset[] {
    const result: Asset[] = [];
    const stack = [folderId];
    while (stack.length) {
      const id = stack.pop()!;
      result.push(...(folderAssets[id] ?? []));
      folders.filter((f) => f.parent_id === id).forEach((f) => stack.push(f.id));
    }
    return result;
  }

  const rootFolders = folders.filter((f) => f.parent_id === null);
  const openAssets = useMemo(() => (openFolder ? assetsInFolderTree(openFolder.id) : []), [openFolder, folders, folderAssets]);

  const close = useCallback(() => setOpenFolder(null), []);
  const next = useCallback(
    () => setCarouselIdx((i) => (openAssets.length ? (i + 1) % openAssets.length : 0)),
    [openAssets.length],
  );
  const prev = useCallback(
    () => setCarouselIdx((i) => (openAssets.length ? (i - 1 + openAssets.length) % openAssets.length : 0)),
    [openAssets.length],
  );

  useEffect(() => {
    if (!openFolder) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openFolder, close, next, prev]);

  return (
    <SubPage
      section="comunicaciones"
      eyebrow="Comunicaciones"
      title="Multimedia"
      intro="Fotos y videos organizados en carpetas. Haz clic en una carpeta para ver la galería."
    >
      {rootFolders.length === 0 ? (
        <p className="text-[var(--color-on-surface-variant)]">Aún no hay contenido multimedia publicado.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 not-prose">
          {rootFolders.map((f) => {
            const inner = assetsInFolderTree(f.id);
            const cover = inner.find((a) => a.mime_type?.startsWith("image/")) ?? inner[0];
            const isActive = openFolder?.id === f.id;
            return (
              <button
                key={f.id}
                onClick={() => {
                  setOpenFolder(isActive ? null : f);
                  setCarouselIdx(0);
                }}
                className={`text-left bg-white border transition-colors overflow-hidden group ${
                  isActive
                    ? "border-[var(--color-brand-coral)] ring-2 ring-[var(--color-brand-coral)]"
                    : "border-[var(--color-outline-variant)] hover:border-[var(--color-brand-coral)]"
                }`}
              >
                <div className="aspect-video bg-[var(--color-surface-container-low)] flex items-center justify-center overflow-hidden">
                  {cover && signed[cover.id] && cover.mime_type?.startsWith("image/") ? (
                    <img src={signed[cover.id]} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <span className="text-5xl">📁</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="font-bold text-[var(--color-brand-purple)] truncate">{f.name}</div>
                  {f.display_date && (
                    <div className="text-xs text-[var(--color-on-surface-variant)] mt-1">
                      {new Date(f.display_date + "T00:00:00").toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  )}
                  <div className="text-xs text-[var(--color-on-surface-variant)] mt-1">
                    {inner.length} archivo{inner.length === 1 ? "" : "s"}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {openFolder && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <div
            className="flex justify-between items-center p-4 bg-[var(--color-brand-purple)] text-white shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold truncate">{openFolder.name}</h3>
            <button
              onClick={close}
              className="text-sm px-3 py-1 border border-white/40 hover:bg-white/10"
            >
              Cerrar ✕
            </button>
          </div>
          {openAssets.length === 0 ? (
            <p className="text-white/70 text-center py-12">Carpeta vacía.</p>
          ) : (
            <div className="flex-1 flex flex-col p-4 min-h-0" onClick={(e) => e.stopPropagation()}>
              <div className="relative flex-1 flex items-center justify-center min-h-0">
                {(() => {
                  const a = openAssets[carouselIdx];
                  if (!a || !signed[a.id]) return <div className="text-white/50">Cargando…</div>;
                  if (a.mime_type?.startsWith("video/")) {
                    return <video src={signed[a.id]} controls className="max-h-full max-w-full" />;
                  }
                  return <img src={signed[a.id]} alt={a.file_name} className="max-h-full max-w-full object-contain" />;
                })()}
                {openAssets.length > 1 && (
                  <>
                    <button
                      onClick={prev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white text-3xl w-12 h-12 rounded-full"
                      aria-label="Anterior"
                    >
                      ‹
                    </button>
                    <button
                      onClick={next}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white text-3xl w-12 h-12 rounded-full"
                      aria-label="Siguiente"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>
              <div className="text-white/70 text-center text-sm mt-3 shrink-0">
                {carouselIdx + 1} / {openAssets.length} — {openAssets[carouselIdx]?.file_name}
              </div>
              {openAssets.length > 1 && (
                <div className="flex gap-2 mt-4 overflow-x-auto pb-2 shrink-0">
                  {openAssets.map((a, i) => (
                    <button
                      key={a.id}
                      onClick={() => setCarouselIdx(i)}
                      className={`shrink-0 w-20 h-20 bg-black overflow-hidden border-2 ${
                        i === carouselIdx ? "border-[var(--color-brand-coral)]" : "border-transparent"
                      }`}
                    >
                      {signed[a.id] && a.mime_type?.startsWith("image/") ? (
                        <img src={signed[a.id]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-xl">▶</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </SubPage>
  );
}
